/**
 * \u516d\u8272\u68cb\u5b50\u89c4\u683c\u8868 \u2014\u2014 \u6e90\u81ea design/art/02-board-mock.html
 * \u989c\u8272\u53d6\u81ea\u7f8e\u672f\u5723\u7ecf v2.7 \u00a73.1\u3002
 * \u4e0d\u8981\u6539\u6570\u503c\uff1b\u8981\u6539\u53bb\u6539 art-bible \u518d\u540c\u6b65\u8fc7\u6765\u3002
 */

import type { ColorblindMode } from '../core/save';

export type ColorKey = 'mercury' | 'venus' | 'earth' | 'mars' | 'saturn' | 'neptune';

/** \u5916\u5f62\u7b7e\u540d\uff08\u56db\u7c7b\uff09\uff1a\u516d\u8fb9\u5f62 / \u516b\u8fb9\u5f62 / \u7eaf\u5706 */
export type ShapeKind = 'hex' | 'oct' | 'circle';

export interface GemSpec {
  name: string;
  cut: string;
  anchor: string;
  light: string;
  mid: string;
  dark: string;
  core: string;
  spec: string;
  rim: string;
  shape: ShapeKind;
  facet: string;
  extra?: string;
  extra2?: string;
  flow?: string;
}

export const GEM_SPECS: Record<ColorKey, GemSpec> = {
  mercury: {
    name: '\u6c34\u661f', cut: '\u516d\u68f1\u9636\u68af\u5207',
    anchor: '\u73af\u5f62\u5c71 \u2192 \u5185\u90e8\u6c14\u6ce1\u5185\u542b\u7269',
    light: '#C7D0DE', mid: '#8E97A6', dark: '#3E4450', core: '#EAF0F8',
    spec: '#FFFFFF', rim: '#DCE4F0', shape: 'hex', facet: 'step',
  },
  venus: {
    name: '\u91d1\u661f', cut: '\u707c\u70ed\u786c\u58f3\u5e03\u6ee1\u5207\u9762',
    anchor: '\u5bc6\u96c6\u5207\u9762\u7f51 \u2192 \u4e2d\u5fc3\u707c\u70ed\u5149\u6838',
    light: '#F8BC74', mid: '#F0A03E', dark: '#A96A22', core: '#FFF0CF',
    spec: '#FFFFFF', rim: '#FFD9A4', shape: 'circle', facet: 'brilliant',
    flow: '#FFDFB4',
  },
  earth: {
    name: '\u5730\u7403', cut: '\u84dd\u6d77\u5927\u5f27\u9762\u7f13\u5207',
    anchor: '\u6d77\u9646\u5f62\u6001 \u2192 \u5c40\u90e8\u5fae\u6c14\u6ce1',
    light: '#9BD6FF', mid: '#3A82DB', dark: '#17408A', core: '#EAF6FF',
    spec: '#FFFFFF', rim: '#B6E2FF', shape: 'circle', facet: 'smooth',
    extra: '#4FA45C', extra2: '#F4F9FF',
  },
  mars: {
    name: '\u706b\u661f', cut: '\u94c1\u9508\u7ea2\u5805\u786c\u5207\u9762',
    anchor: '\u6781\u51a0 \u2192 \u88c2\u7eb9\u5185\u542b\u7269',
    light: '#F2A886', mid: '#C2563A', dark: '#6E2716', core: '#FFE0CF',
    spec: '#FFFFFF', rim: '#FFB9A0', shape: 'oct', facet: 'radiant',
    extra: '#F2F2EF',
  },
  saturn: {
    name: '\u571f\u661f', cut: '\u6de1\u91d1\u9970\u5e26\u7f13\u5207',
    anchor: '\u80cc\u4fa7\u73af\u5e26 \u2192 \u672c\u4f53\u6de1\u91d1\u9970\u5e26 \u2192 \u524d\u4fa7\u73af\u5e26',
    light: '#EDC87E', mid: '#E0B45E', dark: '#7A5416', core: '#FFF3D2',
    spec: '#FFFFFF', rim: '#F7DFA6', shape: 'circle', facet: 'girdle',
    extra: '#F5E7C0',
  },
  neptune: {
    name: '\u6d77\u738b\u661f', cut: '\u6df1\u84dd\u5c9b\u570b\u5207\u9762',
    anchor: '\u79fb\u52a8\u4eae\u65b9 \u2192 \u65e0\u5927\u9ed1\u6591',
    light: '#AAB8FF', mid: '#5566CC', dark: '#282F82', core: '#E7EBFF',
    spec: '#FFFFFF', rim: '#C3CBFF', shape: 'circle', facet: 'girdle-slant',
    extra: '#7C8AE8',
  },
};

/** \u4e3b\u68cb\u989c\u8272\u987a\u5e8f\uff08\u4e0e GDD `COLOR_COUNT = 6` \u4e00\u81f4\uff09 */
export const COLOR_ORDER: ColorKey[] = ['mercury', 'venus', 'earth', 'mars', 'saturn', 'neptune'];

/** \u7f8e\u672f\u5723\u7ecf \u00a73.2 \u6df1\u7a7a\u80cc\u666f */
export const BG = {
  base: '#0B1020',
  linerCore: '#3A2E6E',
  linerMid: '#22305E',
  nebA: '#5B3F9E',
  nebB: '#1E5C7A',
  gridLine: '#2A3552',
  boardStroke: '#3E4E75',
  stardust: '#E8EFFA',
} as const;

/** \u7f8e\u672f\u5723\u7ecf \u00a73.3 UI \u4e09\u9762 */
export const UI = {
  panel: '#161E33',
  panelHighlight: '#2C3A5C',
  groove: '#0E1425',
  textPrimary: '#EAF0FA',
  textSecondary: '#9AA9C6',
  textDisabled: '#5A6478',
} as const;

/** \u7f8e\u672f\u5723\u7ecf \u00a73.4 \u8bed\u4e49\u8272\uff08\u91d1\u8272\u4e25\u7981\u7528\u4e8e\u68cb\u76d8\u4e4b\u4e0a\uff09 */
export const SEMANTIC = {
  positive: '#4FC3F7',
  danger: '#FF5470',
  accentGold: '#F4C542',
  selectionRing: '#7FD8FF',
} as const;

// ──── 色盲调色板映射（S12 SET-03） ────
// 采用 Okabe-Ito 色盲安全配色，覆盖红绿 / 蓝黄三类缺色均互不混淆（SET-03 的"舒适增强"；关闭也能玩）。
// 仅重映射宝石本体色（light/mid/dark/core），保留各星外形 / 锚点 / 内嵌物等形状冗余（美术圣经 §3.5）。
const COLORBLIND_BODY: Record<ColorKey, { light: string; mid: string; dark: string; core: string }> = {
  mercury: { light: '#9BD6FF', mid: '#56B4E9', dark: '#0072B2', core: '#D6F0FF' },
  venus:   { light: '#FCEFA6', mid: '#F0E442', dark: '#BFA000', core: '#FFFBCF' },
  earth:   { light: '#6FD6A8', mid: '#009E73', dark: '#005C45', core: '#CFF6E4' },
  mars:    { light: '#F0A070', mid: '#D55E00', dark: '#8C3D00', core: '#FFD9B0' },
  saturn:  { light: '#F8D08A', mid: '#E69F00', dark: '#946800', core: '#FFEFC4' },
  neptune: { light: '#E6B8D2', mid: '#CC79A7', dark: '#8E4E6E', core: '#F6DCEB' },
};

let activeColorblind: ColorblindMode = 'off';

/** S12 SET-03：设置色盲模式（main 在首帧前与每次变更时调用） */
export function applyColorblindMode(mode: ColorblindMode): void {
  activeColorblind = mode;
}

/**
 * 取某颜色当前应渲染的规格：色盲模式下套用安全调色板（S12 SET-03）。
 * 渲染层 / HUD / 动画导演的颜色取色统一走本函数，保证切换即时一致（E5）。
 */
export function getGemSpec(key: ColorKey): GemSpec {
  const base = GEM_SPECS[key];
  if (activeColorblind === 'off') return base;
  const cb = COLORBLIND_BODY[key];
  return { ...base, light: cb.light, mid: cb.mid, dark: cb.dark, core: cb.core };
}
